import { useState, useEffect } from 'react';
import { getDexfSettings } from '@inter-ikea-storageone/shared/services/dexfSettingsService';

interface UseDexfSettingsReturn {
  dexfSettings: any | null;
  enableSaveToProfile: boolean;
  experienceGalleryBackURL: string | undefined;
  bookAppointmentEnabled: boolean;
  supportBookingLink: string | undefined;
  homePageLink: string | undefined;
}

/**
 * Lightweight version to Load DEXF settings
 *
 *
 * @returns Object containing DEXF settings and computed values
 * @returns dexfSettings - Raw DEXF settings (null while loading)
 * @returns enableSaveToProfile - Whether save to profile is enabled
 * @returns experienceGalleryBackURL - URL for gallery back button
 * @returns bookAppointmentEnabled - Whether booking appointments is enabled
 * @returns supportBookingLink - URL template for booking appointments
 */
export const useDexfSettings = (): UseDexfSettingsReturn => {
  const [dexfSettings, setDexfSettings] = useState<any | null>(null);

  useEffect(() => {
    // Note: getDexfSettings is memoized at the service level, so multiple calls
    // (e.g., from both this hook and ServiceSettingsProvider) share the same cached result
    // We need this for the lightweight landing page to function without the service settings provider
    getDexfSettings().then(settings => {
      setDexfSettings(settings);
    });
  }, []);

  return {
    dexfSettings,
    enableSaveToProfile: dexfSettings?.general?.enableSaveToProfile || false,
    experienceGalleryBackURL: dexfSettings?.gallery?.experienceGalleryBackURL,
    bookAppointmentEnabled: dexfSettings?.general?.bookAppointmentEnabled || false,
    supportBookingLink: dexfSettings?.kompis?.urls?.supportBookingLink,
    homePageLink: dexfSettings?.kompis?.urls?.homePageLink
  };
};
